import React, { useMemo } from "react";
import moment from "moment";
import { useAnalyticsState } from "../../context/analytics";
import ChartDefault from "./ChartDefault";
import ChartAgronomics from "./ChartAgronomics";
import ChartAmbientClimate from "./ChartAmbientClimate";
import ChartAdvisoryLine from "./ChartAdvisoryLine";
import ChartDisease from "./ChartDisease";
import ChartSoilClimate from "./ChartSoilClimate";
import theme from "../../constants/theme";
import ChartMoistureGains from "./ChartMoistureGains";
import ChartStackedLine from "./ChartStackedLine";
import ChartRunOff from "./ChartRunOff";
import ChartAccumulatedRunOff from "./ChartAccumulatedRunOff";
import ChartRainfall from "./ChartRainfall";
import { deltaTThresholds, vpdThresholds } from "./thresholds";

const ChartSelection = ({
  chart,
  data,
  selectedField,
  chartEvents,
  selectedResolution,
}) => {
  const {
    field,
    range: { start_date, end_date },
  } = useAnalyticsState();

  const f = selectedField || field;

  const range = useMemo(() => {
    if (!f?.timezone) {
      return { start_date, end_date };
    }
    return {
      start_date: moment(
        start_date.clone().tz(f.timezone).format("YYYY-MM-DD HH:mm:ss")
      ),
      end_date: moment(
        end_date.clone().tz(f.timezone).format("YYYY-MM-DD HH:mm:ss")
      ),
    };
  }, [end_date, start_date, f?.timezone]);

  if (!chart?.id || !range) return null;

  chartEvents.setRange(range);

  // Per-profile soil_climate charts are keyed as `soil_climate__<profile_id>`.
  // Route them through ChartSoilClimate alongside the legacy id.
  if (chart.id === "soil_climate" || chart.id.startsWith?.("soil_climate__")) {
    const profileId =
      chart.id === "soil_climate" ? "default" : chart.id.slice("soil_climate__".length);
    return (
      <ChartSoilClimate
        data={data}
        fieldId={f.id}
        chartEvents={chartEvents}
        chart={chart}
        field={{
          fieldType: f.field_type,
          timezone: f.timezone,
          rootzone_profiles: f.rootzone_profiles,
        }}
        profileId={profileId}
        range={range}
        theme={theme}
      />
    );
  }

  switch (chart.id) {
    case "ambient_climate":
      return (
        <ChartAmbientClimate
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
        />
      );

    case "agronomics_overview":
      return (
        <ChartAgronomics
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
        />
      );

    case "vpd":
      return (
        <ChartAdvisoryLine
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          thresholds={vpdThresholds}
          dataKey="vpd"
        />
      );

    case "delta_t":
      return (
        <ChartAdvisoryLine
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          thresholds={deltaTThresholds}
          dataKey="delta_t"
        />
      );

    case "mildew":
      return (
        <ChartDisease
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
        />
      );

    case "soil_moisture_gains":
      return (
        <ChartMoistureGains
          data={data}
          fieldId={f.id}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          selectedResolution={selectedResolution}
        />
      );

    case "run_off":
      return (
        <ChartRunOff
          data={data}
          field={f}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          selectedResolution={selectedResolution}
        />
      );

    case "accumulated_run_off":
      return (
        <ChartAccumulatedRunOff
          data={data}
          field={f}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          selectedResolution={selectedResolution}
        />
      );

    case "rainfall":
      return (
        <ChartRainfall
          data={data}
          fieldId={f.id}
          field={f}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          selectedResolution={selectedResolution}
        />
      );

    default:
      if (["soil_moisture", "vic", "soil_temp"].includes(chart.id)) {
        return (
          <ChartStackedLine
            data={data}
            fieldId={f.id}
            field={f}
            chartEvents={chartEvents}
            chart={chart}
            range={range}
            theme={theme}
          />
        );
      }
      return (
        <ChartDefault
          data={data}
          fieldId={f.id}
          field={f}
          chartEvents={chartEvents}
          chart={chart}
          range={range}
          theme={theme}
          selectedResolution={selectedResolution}
        />
      );
  }
};

export default ChartSelection;
